"use client";

import {
	type DefaultImageProps,
	type DefaultLinkProps,
	Heading,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	Price,
	type PriceProps,
	Rating,
	type RatingProps
} from "@pamperedchef/design-system";
import type { HeadingTag } from "@/app/utilities/types";

import * as styles from "./ProductCard.module.scss";

export interface ProductCardProps<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
> {
	name?: string;
	headingTag?: HeadingTag;
	rating: RatingProps;
	price?: PriceProps;
	image?: ImageProps<ImageComponentProps>;
	cta?: LinkProps<LinkComponentProps>;
}

/**
 * Product Card component
 */
export function ProductCard<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
>({
	name,
	headingTag = "h5",
	rating,
	price,
	image,
	cta
}: ProductCardProps<LinkComponentProps, ImageComponentProps>): JSX.Element {
	return (
		<div className={styles["product-card"]}>
			{image ? (
				<Image
					{...image}
					className={styles["product-card__image"]}
					objectFit="cover"
					aspectRatio="1:1"
					alt={image.alt || ""}
				/>
			) : (
				<div className={styles["product-card__no-image"]} />
			)}

			<div className={styles["product-card__content"]}>
				<Heading
					styledAs="h5"
					tag={headingTag}
					className={styles["product-card__title"]}
				>
					{name || ""}
				</Heading>
				<Rating
					rating={rating?.rating}
					label={rating?.label}
					size="small"
				/>
				<Price productValue={price?.productValue} />

				{cta?.href && cta?.children && (
					<Link<LinkComponentProps>
						{...cta}
						className={styles["product-card__cta"]}
						kind="secondary"
						size="sm"
					/>
				)}
			</div>
		</div>
	);
}
